import React, {ChangeEvent, useEffect, useState} from "react";
import apiService from "../services/api-service";
import {useFetchCurrentEvent} from "./useFetchCurrentEvent";
import {TFetchedNewsDto, TNewsDto} from "../types/contracts";

export const useAdminNewsConfigurator = () => {
    const {event, setEvent} = useFetchCurrentEvent();
    const [fetchedNews, setFetchedNews] = useState<TFetchedNewsDto[]>([]);
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!event) return;

        const fetchNews = async () => {
            setIsLoading(true);
            try {
                const response = await apiService.getNews(event.id);
                const newsData = response.data as TFetchedNewsDto[];
                if (!Array.isArray(newsData)) {
                    throw new Error("Invalid news format");
                }
                setFetchedNews(newsData);
                setHasError(false);
            } catch (err) {
                console.error("Error loading news:", err);
                setHasError(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchNews();
    }, [event]);

    const handleNewsChange = (index: number, e: ChangeEvent<HTMLTextAreaElement>) => {
        const {name, value} = e.target;
        setFetchedNews((prev) => {
            const updateNews = [...prev];
            if (!updateNews[index]) return prev;
            updateNews[index] = {...updateNews[index], [name]: value};
            return updateNews;
        })
    };

    const addNews = async () => {
        if (!event?.id) {
            console.error("Cannot add news without an event ID");
            alert("Nejdříve musíte vytvořit událost");
            return;
        }

        const newNewsItem: TNewsDto = {
            eventId: event.id,
            headline: "",
            body: "",
        };

        try {
            const response = await apiService.createNews<TNewsDto, TFetchedNewsDto>(newNewsItem);
            const createdNews = response.data;

            setFetchedNews((prevNews) => [...prevNews, createdNews]);
        } catch (error) {
            console.error("Failed to create news:", error);
            alert("Nepodařilo se vytvořit novou aktualitu");
        }
    };

    const removeNews = async (index: number) => {
        const currentNews = [...fetchedNews];
        const newsToDelete = currentNews[index];

        if (!newsToDelete || !newsToDelete.id) return;

        try {
            await apiService.deleteNews(newsToDelete.id);
            console.info(`Deleted news with id: ${newsToDelete.id}`);

            // Update the state after successful deletion
            setFetchedNews((prevNews) =>
                prevNews.filter((_, i) => i !== index)
            );
        } catch (error) {
            console.error(`Failed to delete news with id: ${newsToDelete.id}`, error);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!event) {
            console.error("Event is undefined");
            alert("Událost není vytvořena");
            return;
        }

        setEvent(event);

        try {
            await Promise.all(
                fetchedNews.map((newsItem) =>
                    apiService.updateNews(newsItem.id, newsItem)
                )
            );
            alert("Aktuality byly úspěšně uloženy");
        } catch (error) {
            console.error("Chyba při ukládání aktualit", error);
            alert("Nepodařilo se uložit aktuality");
        }
    };

    return {
        event,
        fetchedNews,
        hasError,
        isLoading,
        handleNewsChange,
        addNews,
        removeNews,
        handleSubmit,
    };
};
